import { useMutation } from "@tanstack/react-query";
import { useAccount } from "hooks/useAccount";
import { updateBillingFetcher } from "queries/account.queries";
import React, { useEffect, useState } from "react";
import { View } from "react-native";
import toast from "utils/toast";
import Button from "./Button";
import StripePaymentForm, {
  StripeTokenError,
  StripeTokenResponse,
} from "./StripePaymentForm/StripePaymentForm";
import i18n from "plugins/localization";

interface ConfirmPurchaseButtonProps {
  buttonText?: string;
  onPress?: () => void;
  disabled?: boolean;
  loading?: boolean;
  charge: number;
}

const ConfirmPurchaseButton: React.FC<ConfirmPurchaseButtonProps> = ({
  charge,
  buttonText = "Confirm Purchase",
  disabled,
  onPress,
  loading,
}) => {
  const [shouldShowPaymentForm, setShouldShowPaymentForm] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const { account, fetchAccount } = useAccount();
  const { mutateAsync: updateBilling } = useMutation(updateBillingFetcher);
  const handleTokenReceived = async (
    error: StripeTokenError | undefined,
    response?: StripeTokenResponse
  ) => {
    try {
      if (error) {
        toast.error(error.error.message);
        return;
      }
      setSendingRequest(true);
      await updateBilling({
        accountId: account!.id,
        token: response!,
      });
      fetchAccount();
      onPress && onPress();
    } finally {
      setSendingRequest(false);
    }
  };

  useEffect(() => {
    setShouldShowPaymentForm(account?.billingLast4 === null && charge > 0);
  }, [account, charge]);

  return (
    <View>
      {shouldShowPaymentForm ? (
        <StripePaymentForm
          disabled={disabled}
          onTokenReceived={handleTokenReceived}
          loading={loading || sendingRequest}
          btnText={buttonText}
        ></StripePaymentForm>
      ) : (
        <Button
          disabled={disabled}
          onPress={onPress}
          loading={loading}
          testID="confirm-purchase-button"
        >
          {buttonText}
        </Button>
      )}
    </View>
  );
};

export default ConfirmPurchaseButton;
